import React, { useContext, useEffect, useReducer, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Button, IconButton, Typography, Grid, Box, Chip, Avatar,
  Tooltip, Fade, Card, CardContent, LinearProgress
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, Add, TrendingUp, Forum, CheckCircle, FlashOn } from "@material-ui/icons";
import PromptModal from "../../components/PromptModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import koraxRobot from "../../assets/cavia-robot.png";

const useStyles = makeStyles((theme) => ({
  pageRoot: {
    flex: 1, width: "100%",
    padding: theme.spacing(2),
    height: "calc(100% - 48px)",
    display: "flex", flexDirection: "column",
    gap: theme.spacing(2), overflowY: "auto",
    background: theme.palette.type === "dark" ? "#080E1C" : "#F8FAFC",
    ...theme.scrollbarStyles,
  },

  // ===== HERO =====
  hero: {
    borderRadius: 22,
    padding: theme.spacing(2.5, 3.5),
    minHeight: 138,
    marginBottom: theme.spacing(3),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg,#0D1626 0%,#111D33 100%)"
      : "linear-gradient(135deg,#F8FBFF 0%,#FFFFFF 52%,#EDF6FF 100%)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,102,255,0.14)"}`,
    position: "relative",
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "0 14px 34px rgba(0,0,0,0.18)"
      : "0 14px 38px rgba(15,23,42,0.08)",
    flexShrink: 0,
  },
  heroBg1: { display: "none" },
  heroBg2: { display: "none" },
  heroLeft: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    paddingRight: theme.spacing(3),
  },
  heroActiveBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)",
    borderRadius: 20, padding: "3px 10px", marginBottom: theme.spacing(1),
    width: "fit-content",
  },
  heroActiveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#00D084",
    boxShadow: "0 0 8px #00D084",
    animation: "$pulse 2s infinite",
  },
  heroActiveTxt: {
    fontSize: "0.72rem",
    fontWeight: 700,
    color: "#00D084",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
  },
  heroTitle: {
    fontWeight: 700, fontSize: "1.1rem", lineHeight: 1.2,
    letterSpacing: "-0.3px",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#0F172A",
    marginBottom: theme.spacing(0.25),
  },
  heroTitleBlue: {
    color: "#0066FF",
  },
  heroSubtitle: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.55)" : "rgba(10,22,40,0.6)",
    lineHeight: 1.6,
    maxWidth: 400,
    marginBottom: theme.spacing(1.5),
  },
  heroBtn: {
    borderRadius: 10, fontWeight: 600, fontSize: "0.82rem",
    padding: theme.spacing(0.9, 2.5),
    background: "#1D4ED8", color: "#ffffff", boxShadow: "none",
    "&:hover": { background: "#1E40AF" },
    transition: "all 0.15s ease",
  },
  heroRobot: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  robotImg: {
    width: "100%",
    maxWidth: 160,
    filter: "drop-shadow(0 20px 40px rgba(0,102,255,0.3))",
    animation: "$float 4s ease-in-out infinite",
  },
  heroMetrics: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    justifyContent: "center",
  },
  metricCard: {
    borderRadius: 12, padding: theme.spacing(1.5, 2),
    background: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "#F8FAFC",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    boxShadow: "none",
  },
  metricIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    marginBottom: theme.spacing(0.5),
    fontSize: "1rem",
  },
  metricNum: {
    fontWeight: 800,
    fontSize: "1.6rem",
    lineHeight: 1,
    color: theme.palette.type === "dark" ? "#ffffff" : "#0A1628",
  },
  metricLabel: {
    fontSize: "0.72rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgba(10,22,40,0.5)",
    marginTop: 2,
  },
  metricTrend: {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#00D084",
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginTop: 4,
  },

  // ===== AGENTS SECTION =====
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
  sectionTitle: {
    fontWeight: 800,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
  },
  sectionSubtitle: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  addAgentBtn: {
    borderRadius: 10, fontWeight: 600, fontSize: "0.82rem",
    padding: theme.spacing(0.9, 2.5),
    background: "#1D4ED8", color: "#ffffff", boxShadow: "none",
    "&:hover": { background: "#1E40AF" },
  },

  // ===== AGENT CARDS =====
  agentsGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
  gap: theme.spacing(1.5),
},

agentCard: {
  position: "relative", overflow: "hidden",
  borderRadius: 16,
  border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
  background: theme.palette.type === "dark" ? "#0D1626" : "#ffffff",
  padding: theme.spacing(2),
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  transition: "all 0.15s ease",
  "&:before": {
    content: '""', position: "absolute",
    left: 0, top: 0, bottom: 0, width: 2,
    background: "#1D4ED8", opacity: 0.7,
  },
  "&:hover": {
    transform: "translateY(-1px)",
    borderColor: "rgba(29,78,216,0.2)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  },
},

agentCardHeader: {
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.25),
},

agentAvatar: {
  width: 42,
  height: 42,
  borderRadius: 12,
  background: "linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)",
  border: "1px solid rgba(96,165,250,0.25)",
  fontWeight: 800,
  fontSize: "0.95rem",
  color: "#ffffff",
  flexShrink: 0,
  boxShadow: "0 10px 24px rgba(29,78,216,0.18)",
},

agentName: {
  fontWeight: 700,
  fontSize: "0.92rem",
  color: theme.palette.text.primary,
  letterSpacing: "-0.01em",
},

agentQueue: {
  background: theme.palette.type === "dark"
    ? "rgba(37,99,235,0.12)"
    : "rgba(37,99,235,0.07)",
  color: theme.palette.type === "dark" ? "#93C5FD" : "#1D4ED8",
  border: "1px solid rgba(37,99,235,0.14)",
  fontWeight: 600,
  fontSize: "0.68rem",
  height: 20,
  marginTop: 4,
},

activeBadge: {
  background: "rgba(16,185,129,0.10)",
  color: "#10B981",
  border: "1px solid rgba(16,185,129,0.18)",
  fontWeight: 700,
  fontSize: "0.66rem",
  height: 20,
  marginLeft: "auto",
},

agentTokens: {
  background: theme.palette.type === "dark"
    ? "rgba(245,158,11,0.10)"
    : "rgba(245,158,11,0.08)",
  color: "#D97706",
  border: "1px solid rgba(245,158,11,0.18)",
  fontWeight: 600,
  fontSize: "0.68rem",
  height: 20,
},

agentDivider: {
  height: 1,
  background: theme.palette.type === "dark"
    ? "rgba(148,163,184,0.10)"
    : "rgba(15,23,42,0.06)",
  margin: theme.spacing(1.25, 0),
},

agentActions: {
  position: "relative",
  zIndex: 1,
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.25),
},

editBtn: {
  flex: 1,
  borderRadius: 10,
  fontWeight: 650,
  fontSize: "0.74rem",
  border: "1px solid rgba(37,99,235,0.18)",
  color: theme.palette.type === "dark" ? "#93C5FD" : "#1D4ED8",
  padding: theme.spacing(0.65, 0),
  textTransform: "none",
  "&:hover": {
    background: "rgba(37,99,235,0.08)",
    borderColor: "rgba(37,99,235,0.28)",
  },
},

deleteBtn: {
  borderRadius: 10,
  border: "1px solid rgba(239,68,68,0.16)",
  color: "#EF4444",
  padding: theme.spacing(0.6, 1.1),
  "&:hover": {
    background: "rgba(239,68,68,0.07)",
  },
},
  

  // ===== ADD CARD =====
  addCard: {
    borderRadius: 16,
    border: `1.5px dashed ${theme.palette.type === "dark" ? "rgba(29,78,216,0.3)" : "rgba(29,78,216,0.2)"}`,
    background: theme.palette.type === "dark" ? "rgba(29,78,216,0.04)" : "rgba(29,78,216,0.02)",
    padding: theme.spacing(2.5),
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    cursor: "pointer", minHeight: 140,
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "rgba(29,78,216,0.4)",
      background: "rgba(29,78,216,0.05)",
    },
  },
  addCardIcon: {
    width: 48,
    height: 48,
    background: "linear-gradient(135deg, rgba(0,102,255,0.1) 0%, rgba(0,163,255,0.1) 100%)",
    border: "2px solid rgba(0,102,255,0.2)",
    color: "#0066FF",
    marginBottom: theme.spacing(1.5),
    fontSize: "1.5rem",
  },
  addCardTitle: {
    fontWeight: 700,
    fontSize: "0.88rem",
    color: "#0066FF",
    marginBottom: theme.spacing(0.5),
  },
  addCardSubtitle: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },

  // ===== FOOTER BANNER =====
  footerBanner: {
    borderRadius: 14, padding: theme.spacing(2, 2.5),
    background: theme.palette.type === "dark" ? "#0D1626" : "#ffffff",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E8EDF5"}`,
    display: "flex", alignItems: "center", gap: theme.spacing(2),
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  footerIcon: {
    width: 44,
    height: 44,
    background: "linear-gradient(135deg, #0066FF 0%, #00A3FF 100%)",
    color: "#fff",
    flexShrink: 0,
    fontSize: "1.2rem",
  },
  footerTitle: {
    fontWeight: 700,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  footerSubtitle: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  footerBtn: {
    marginLeft: "auto",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: "0.78rem",
    border: "1px solid rgba(0,102,255,0.3)",
    color: "#0066FF",
    padding: theme.spacing(0.8, 2),
    flexShrink: 0,
    "&:hover": { background: "rgba(0,102,255,0.06)" },
  },

  "@keyframes pulse": {
    "0%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.5, transform: "scale(1.3)" },
    "100%": { opacity: 1, transform: "scale(1)" },
  },
  "@keyframes float": {
    "0%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-12px)" },
    "100%": { transform: "translateY(0px)" },
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_PROMPTS") {
    const prompts = action.payload;
    const newPrompts = [];
    prompts.forEach((prompt) => {
      const idx = state.findIndex((p) => p.id === prompt.id);
      if (idx !== -1) { state[idx] = prompt; } else { newPrompts.push(prompt); }
    });
    return [...state, ...newPrompts];
  }
  if (action.type === "UPDATE_PROMPTS") {
    const prompt = action.payload;
    const idx = state.findIndex((p) => p.id === prompt.id);
    if (idx !== -1) { state[idx] = prompt; return [...state]; }
    return [prompt, ...state];
  }
  if (action.type === "DELETE_PROMPT") {
    const id = action.payload;
    const idx = state.findIndex((p) => p.id === id);
    if (idx !== -1) { state.splice(idx, 1); }
    return [...state];
  }
  if (action.type === "RESET") { return []; }
};

const getAgentScore = prompt => {
  let score = 42;
  if (prompt?.name) score += 12;
  if (prompt?.prompt) score += 18;
  if (prompt?.queue?.name) score += 10;
  if (prompt?.maxMessages) score += 8;
  if (prompt?.temperature) score += 5;
  return Math.min(score, 98);
};

const getAgentEmoji = prompt => {
  const name = String(prompt?.name || "").toLowerCase();
  if (name.includes("suporte")) return "🛟";
  if (name.includes("cancel")) return "🛡️";
  if (name.includes("pós") || name.includes("pos")) return "🤝";
  if (name.includes("venda") || name.includes("comercial")) return "💰";
  return "🤖";
};

const Prompts = () => {
  const classes = useStyles();
  const [prompts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [chooseModeOpen, setChooseModeOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();

  useEffect(() => {
    console.log("PROMPTS PAGE MOUNTED", window.location.pathname);
  }, []);

  const companyId = user.companyId;

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useOpenAi) {
        console.warn("Bypass temporário da verificação de plano (onboarding).");
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/prompt");
        dispatch({ type: "LOAD_PROMPTS", payload: data.prompts });
      } catch (err) { toastError(err); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    const onPromptEvent = (data) => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_PROMPTS", payload: data.prompt });
      if (data.action === "delete") dispatch({ type: "DELETE_PROMPT", payload: data.promptId });
    };
    socket.on(`company-${companyId}-prompt`, onPromptEvent);
    
    return () => { socket.off(`company-${companyId}-prompt`, onPromptEvent); };
  }, [socket, companyId]);

  const ChooseModeModal = () => {
    if (!chooseModeOpen) return null;

    return (      
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000
      }}>
        <div style={{
          background: "#fff",
          borderRadius: 20,
          padding: 32,
          width: "100%",
          maxWidth: 900
        }}>
          
          <Typography style={{ fontWeight: 800, fontSize: 22, marginBottom: 8 }}>
            Treine sua Korax IA em poucos minutos
          </Typography>

          <Typography style={{ color: "#555", marginBottom: 24 }}>
            Responda perguntas simples sobre seu negócio e deixe a Korax IA pronta para atender, qualificar e conduzir seus leads de forma humanizada.
          </Typography>

          <Grid container spacing={2}>

            <Grid item xs={12} md={6}>
              <Card style={{
                borderRadius: 16,
                border: "2px solid #0066FF",
                boxShadow: "0 10px 30px rgba(0,102,255,0.15)"
              }}>
                <CardContent>

                  <Chip label="RECOMENDADO" style={{
                    background: "#0066FF",
                    color: "#fff",
                    marginBottom: 10
                  }} />

                  <Typography style={{ fontWeight: 700, fontSize: 18 }}>
                    Construtor Inteligente
                  </Typography>

                  <Typography style={{ fontSize: 14, color: "#666", margin: "10px 0 20px" }}>
                    Crie seu agente passo a passo, sem precisar escrever prompt. A Korax IA monta a estrutura ideal com base nas respostas do seu negócio.
                  </Typography>

                  <Button
                    fullWidth
                    style={{
                      background: "#0066FF",
                      color: "#fff",
                      borderRadius: 12
                    }}
                    onClick={handleStartInteligente}
                  >
                    Começar treinamento guiado
                  </Button>

                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card style={{
                borderRadius: 16,
                border: "1px solid #ddd"
              }}>
                <CardContent>

                  <Chip label="AVANÇADO" style={{
                    background: "#eee",
                    marginBottom: 10
                  }} />

                  <Typography style={{ fontWeight: 700, fontSize: 18 }}>
                    Configuração por Prompt
                  </Typography>

                  <Typography style={{ fontSize: 14, color: "#666", margin: "10px 0 20px" }}>
                    Para usuários que desejam editar manualmente o comportamento da IA usando instruções personalizadas.
                  </Typography>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleStartPrompt}
                  >
                    Configurar manualmente
                  </Button>

                </CardContent>
              </Card>
            </Grid>

          </Grid>

          <Box mt={2} textAlign="center">
            <Button onClick={() => setChooseModeOpen(false)}>
              Cancelar
            </Button>
          </Box>

        </div>
      </div>
    );
  };

  const getInitials = (name) => name ? name.slice(0, 2).toUpperCase() : "AI";
  const handleOpenNew = () => {
  setSelectedPrompt(null);
  setChooseModeOpen(true);
};
  // Rota temporária do Construtor Inteligente da Korax IA.
  // Quando a tela oficial existir, altere esta rota aqui.
  const CAVIA_INTELIGENTE_ROUTE = "/korax-ia/treinamento";

  const handleStartInteligente = () => {
    setChooseModeOpen(false);
    history.push(CAVIA_INTELIGENTE_ROUTE);
  };

  const handleStartPrompt = () => {
    setChooseModeOpen(false);
    setSelectedPrompt(null);
    setPromptModalOpen(true);
  };
  const handleEdit = (prompt) => {
    let builder = prompt?.builderConfig;
    if (typeof builder === "string") {
      try { builder = JSON.parse(builder); } catch (_) { builder = null; }
    }
    if (builder?.config) {
      history.push(`/korax-ia/treinamento/${prompt.id}`);
      return;
    }
    setSelectedPrompt(prompt);
    setPromptModalOpen(true);
  };
  const handleDeletePrompt = async (promptId) => {
    try {
      const { data } = await api.delete(`/prompt/${promptId}`);
      toast.info(i18n.t(data.message));
    } catch (err) { toastError(err); }
    setSelectedPrompt(null);
  };

  return (
    <div className={classes.pageRoot}>
      <ConfirmationModal
        title={selectedPrompt && `Excluir ${selectedPrompt.name}?`}
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setSelectedPrompt(null); }}
        onConfirm={() => handleDeletePrompt(selectedPrompt.id)}
      >
        {i18n.t("prompts.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <PromptModal open={promptModalOpen} onClose={() => { setPromptModalOpen(false); setSelectedPrompt(null); }} promptId={selectedPrompt?.id} />
      <ChooseModeModal />
      {user.profile === "user" ? <ForbiddenPage /> : (
        <>
          {/* ===== HERO PREMIUM ===== */}
          <div className={classes.hero} style={{
            padding: 24,
            borderRadius: 24,
            background: "linear-gradient(135deg,#071B4D 0%,#005BFF 54%,#00A3FF 100%)",
            color: "#fff",
            boxShadow: "0 24px 60px rgba(0,102,255,.22)",
            overflow: "hidden",
            position: "relative"
          }}>
            <div style={{
              position: "absolute",
              right: -80,
              top: -80,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,.14)",
              filter: "blur(2px)"
            }} />
            <div style={{
              display: "grid",
              gridTemplateColumns: window.innerWidth <= 768 ? "1fr" : "1fr 430px",
              gap: window.innerWidth <= 768 ? 18 : 22,
              alignItems: "center",
              position: "relative",
              zIndex: 1
            }}>
              <div style={{
                display: "flex",
                alignItems: window.innerWidth <= 768 ? "flex-start" : "center",
                gap: 16,
                flexDirection: window.innerWidth <= 768 ? "column" : "row"
              }}>
                <img src={koraxRobot} alt="Korax IA" style={{
                  width: window.innerWidth <= 768 ? 96 : 86,
                  height: window.innerWidth <= 768 ? 96 : 86,
                  objectFit: "contain",
                  filter: "drop-shadow(0 12px 26px rgba(0,0,0,.25))"
                }} />
                <div>
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(34,197,94,.16)",
                    border: "1px solid rgba(167,243,208,.36)",
                    color: "#D1FAE5",
                    borderRadius: 999,
                    padding: "6px 10px",
                    fontWeight: 900,
                    fontSize: 12,
                    marginBottom: 10
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#22C55E", boxShadow: "0 0 0 6px rgba(34,197,94,.18)" }} />
                    IA Korax Online
                  </div>
                  <Typography style={{
                  fontWeight: 950,
                  fontSize: window.innerWidth <= 768 ? 34 : 28,
                  lineHeight: 1.08,
                  letterSpacing: -1,
                  color: "inherit"
                }}>
                    Central de Agentes Inteligentes
                  </Typography>
                  <Typography style={{
                    fontSize: window.innerWidth <= 768 ? 22 : 14,
                    opacity: .86,
                    lineHeight: 1.35,
                    marginTop: 8,
                    maxWidth: 650
                  }}>
                    Treine, monitore e evolua agentes de IA para atendimento, vendas, suporte e pós-venda em um só lugar.
                  </Typography>
                </div>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: window.innerWidth <= 768 ? "1fr 1fr" : "1fr 1fr",
                gap: 10,
                width: "100%"
              }}>
                {[
                  { icon: "🤖", label: "Agentes ativos", value: prompts.length },
                  { icon: "⚡", label: "Operação", value: "24h" },
                  { icon: "💬", label: "Respostas IA", value: "Online" },
                  { icon: "🧠", label: "Treinamento", value: "Premium" },
                ].map((m, i) => (
                  <div key={i} style={{
                    borderRadius: window.innerWidth <= 768 ? 16 : 18,
                    padding: window.innerWidth <= 768 ? 12 : 14,
                    background: "rgba(255,255,255,.13)",
                    border: "1px solid rgba(255,255,255,.18)",
                    backdropFilter: "blur(8px)"
                  }}>
                    <div style={{ fontSize: 20 }}>{m.icon}</div>
                    <div style={{ fontWeight: 950, fontSize: window.innerWidth <= 768 ? 22 : 20, marginTop: 4 }}>{m.value}</div>
                    <div style={{ fontSize: window.innerWidth <= 768 ? 13 : 11, opacity: .78, fontWeight: 700 }}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ===== AGENTS ===== */}
          <div>
            <div className={classes.sectionHeader}>
              <div>
                <Typography className={classes.sectionTitle}>Seus Agentes de IA</Typography>
                <Typography className={classes.sectionSubtitle}>Gerencie e monitore seus agentes inteligentes</Typography>
              </div>
              <div style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                width: window.innerWidth <= 768 ? "100%" : "auto",
                justifyContent: window.innerWidth <= 768 ? "stretch" : "flex-start"
              }}>
                <Button variant="outlined" onClick={() => history.push("/korax-ia/treinamento")}
                  style={{ borderRadius: 10, fontWeight: 700, fontSize: "0.78rem", padding: "8px 14px", border: "1px solid rgba(0,102,255,0.3)", color: "#0066FF", textTransform: "none" }}
                  startIcon={<span style={{fontSize:13}}>🧠</span>}>
                  Construtor
                </Button>
                <Button className={classes.addAgentBtn} startIcon={<Add />} onClick={handleOpenNew} style={{ fontSize: "0.78rem", padding: "8px 14px" }}>
                  + Novo agente
                </Button>
              </div>
            </div>

            <div className={classes.agentsGrid}>
              {loading && [1,2].map(i => (
                <div key={i} className={classes.agentCard}>
                  <TableRowSkeleton columns={1} />
                </div>
              ))}
              {prompts.map((prompt) => {
                const score = getAgentScore(prompt);
                return (
                <Fade in key={prompt.id}>
                  <div className={classes.agentCard} style={{
                    borderRadius: 22,
                    padding: 0,
                    overflow: "hidden",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 16px 38px rgba(15,23,42,.07)",
                    background: "#fff"
                  }}>
                    <div style={{
                      padding: 18,
                      background: "linear-gradient(180deg,#FFFFFF 0%,#F8FAFC 100%)",
                      borderBottom: "1px solid #EEF2F7"
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <Avatar className={classes.agentAvatar} style={{
                          width: 52,
                          height: 52,
                          background: "linear-gradient(135deg,#071B4D,#005BFF)",
                          boxShadow: "0 12px 22px rgba(0,102,255,.18)",
                          fontSize: 22
                        }}>
                          {getAgentEmoji(prompt)}
                        </Avatar>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Typography className={classes.agentName} style={{ fontSize: 16, fontWeight: 950, letterSpacing: -.3 }}>
                            {prompt.name}
                          </Typography>
                          <div style={{ display: "flex", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
                            <Chip label={prompt.queue?.name || "Sem setor"} className={classes.agentQueue} size="small" />
                            <Chip label="● Online" className={classes.activeBadge} size="small" />
                          </div>
                        </div>
                        <div style={{
                          borderRadius: 14,
                          background: "rgba(34,197,94,.08)",
                          border: "1px solid rgba(34,197,94,.18)",
                          color: "#16A34A",
                          padding: "7px 10px",
                          fontSize: 11,
                          fontWeight: 900
                        }}>
                          Ativo
                        </div>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                          <span style={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}>Qualidade do treinamento</span>
                          <span style={{ fontSize: 12, color: "#005BFF", fontWeight: 950 }}>{score}%</span>
                        </div>
                        <div style={{ height: 8, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                          <div style={{ width: `${score}%`, height: "100%", background: "linear-gradient(90deg,#005BFF,#00A3FF)", borderRadius: 999 }} />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 16 }}>
                        {[
                          { icon: "💬", value: "24h", label: "Atende" },
                          { icon: "🎯", value: prompt.queue?.name ? "Setor" : "Geral", label: "Uso" },
                          { icon: "🧠", value: score > 75 ? "Forte" : "Ajustar", label: "Treino" },
                        ].map((s, i) => (
                          <div key={i} style={{ borderRadius: 14, background: "#F8FAFC", border: "1px solid #EEF2F7", padding: 10, textAlign: "center" }}>
                            <div style={{ fontSize: 16 }}>{s.icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 950, color: "#0F172A", marginTop: 3 }}>{s.value}</div>
                            <div style={{ fontSize: 10, color: "#64748B", fontWeight: 800 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{
                      padding: 14,
                      display: "grid",
                      gridTemplateColumns: window.innerWidth <= 768 ? "1fr 1fr 42px" : "1.2fr 1fr 42px",
                      gap: 9
                    }}>
                      <Button
                        variant="contained"
                        onClick={() => history.push(`/korax-ia/treinamento/${prompt.id}`)}
                        style={{
                          borderRadius: 12,
                          textTransform: "none",
                          fontWeight: 900,
                          fontSize: "0.76rem",
                          background: "linear-gradient(135deg,#0066FF,#00A3FF)",
                          color: "#fff",
                          boxShadow: "0 10px 22px rgba(0,102,255,.18)",
                          padding: "9px 12px",
                          minWidth: "unset"
                        }}
                      >
                        🧠 Treinar IA
                      </Button>

                      <Button className={classes.editBtn} variant="outlined" startIcon={<Edit style={{ fontSize: 14 }} />} onClick={() => handleEdit(prompt)}
                        style={{ borderRadius: 12, fontWeight: 900, textTransform: "none" }}>
                        Configurar
                      </Button>

                      <Tooltip title="Excluir">
                        <IconButton size="small" className={classes.deleteBtn} onClick={() => { setSelectedPrompt(prompt); setConfirmModalOpen(true); }}
                          style={{ borderRadius: 12 }}>
                          <DeleteOutline style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </Fade>
              )})}

              {/* ADD CARD */}
              <div className={classes.addCard} onClick={handleOpenNew}>
                <Avatar className={classes.addCardIcon}><Add style={{ fontSize: 24 }} /></Avatar>
                <Typography className={classes.addCardTitle}>Criar novo agente</Typography>
                <Typography className={classes.addCardSubtitle}>Personalize um agente para o seu negócio</Typography>
              </div>
            </div>
          </div>

          {/* ===== FOOTER BANNER ===== */}
          <div className={classes.footerBanner}>
            <Avatar className={classes.footerIcon}><FlashOn /></Avatar>
            <div>
              <Typography className={classes.footerTitle}>Korax IA aprende, evolui e entrega resultados.</Typography>
              <Typography className={classes.footerSubtitle}>Nossos agentes utilizam IA treinada e humanizada para criar conversas naturais e gerar mais vendas.</Typography>
            </div>
            <Button className={classes.footerBtn} variant="outlined" onClick={handleOpenNew}>
              Saiba mais →
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Prompts;
